import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function TableNo24() {
  const pdfRef = useRef();

  // Data — Male Count by Age Group (2024)
  const ageGroupData = [
    { ageGroup: "14–18", maleCount2024: 920 },
    { ageGroup: "19–21", maleCount2024: 3248 },
    { ageGroup: "22–24", maleCount2024: 5180 },
    { ageGroup: "25–34", maleCount2024: 21678 },
    { ageGroup: "35–44", maleCount2024: 21908 },
    { ageGroup: "45–54", maleCount2024: 19361 },
    { ageGroup: "55–64", maleCount2024: 14970 },
    { ageGroup: "65–99", maleCount2024: 5676 },
  ];

  // Summary statistics
  const totalCount = ageGroupData.reduce((sum, d) => sum + d.maleCount2024, 0);
  const topGroup = ageGroupData.reduce((max, d) =>
    d.maleCount2024 > max.maleCount2024 ? d : max
  );
  const lowGroup = ageGroupData.reduce((min, d) =>
    d.maleCount2024 < min.maleCount2024 ? d : min
  );

  // Chart setup
  const chartData = {
    labels: ageGroupData.map((d) => d.ageGroup),
    datasets: [
      {
        label: "Male Count (2024)",
        data: ageGroupData.map((d) => d.maleCount2024),
        backgroundColor: "rgba(14, 165, 233, 0.8)",
        borderRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: { color: "#111827", font: { size: 13 } },
      },
      title: {
        display: true,
        text: "📊 Male Count by Age Group (2024)",
        font: { size: 16, weight: "bold" },
        color: "#111827",
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.parsed.y.toLocaleString("en-US")} males`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { callback: (v) => v.toLocaleString("en-US"), color: "#4B5563" },
        title: {
          display: true,
          text: "Male Count",
          color: "#111827",
          font: { size: 14, weight: "bold" },
        },
      },
      x: {
        ticks: { color: "#4B5563" },
        title: {
          display: true,
          text: "Age Groups",
          color: "#111827",
          font: { size: 14, weight: "bold" },
        },
      },
    },
  };

  // PDF Export
  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2, // higher for sharper text
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      // A4 size in mm
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Convert canvas height to mm
      const imgWidth = pageWidth - 10; // leave small margins
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      // Header
      const addHeader = () => {
        pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      // Footer
      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right"
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // More pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Footer for the last page
      pdf.line(10, 260, 200, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
      pdf.text("20250916.v3.198", 148, footerY + 10);
      pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("male_count_age_group.pdf");
      // input.style.width = originalWidth;
    });
  };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-blue-50 via-white to-blue-100 rounded-xl shadow-lg"
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Male Count by Age Group (2024)
        </h2>

        {/* Insight Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Total Male Count (2024)</p>
            <p className="text-xl font-bold text-blue-800">
              {totalCount.toLocaleString("en-US")}
            </p>
          </div>
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Highest Count Age Group</p>
            <p className="text-xl font-bold text-green-800">
              {topGroup.ageGroup} (
              {topGroup.maleCount2024.toLocaleString("en-US")})
            </p>
          </div>
          <div className="p-4 bg-red-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Lowest Count Age Group</p>
            <p className="text-xl font-bold text-red-700">
              {lowGroup.ageGroup} (
              {lowGroup.maleCount2024.toLocaleString("en-US")})
            </p>
          </div>
        </div>

        {/* Table + Chart Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="overflow-x-auto rounded-lg shadow-md">
            <table className="w-full border-collapse text-center">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Age Group</th>
                  <th className="p-2">Male Count (2024)</th>
                </tr>
              </thead>
              <tbody>
                {ageGroupData.map((row, i) => {
                  const highlight =
                    row.ageGroup === topGroup.ageGroup
                      ? "bg-green-100"
                      : row.ageGroup === lowGroup.ageGroup
                        ? "bg-red-100"
                        : "bg-white";
                  return (
                    <tr key={i} className={`${highlight} hover:bg-gray-50`}>
                      <td className="p-2 font-semibold">{row.ageGroup}</td>
                      <td className="p-2 text-blue-700 font-medium">
                        {row.maleCount2024.toLocaleString("en-US")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>

        {/* Summary Note */}
        <div className="text-center mt-4 text-gray-700">
          <p className="text-sm">
            📌 The{" "}
            <span className="font-semibold text-green-700">
              {topGroup.ageGroup}
            </span>{" "}
            group represents the highest male employment in 2024, while{" "}
            <span className="font-semibold text-red-700">
              {lowGroup.ageGroup}
            </span>{" "}
            holds the smallest share.
          </p>
        </div>
      </div>

      {/* PDF Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
